import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";

const SESSIONS_KEY = "fnr-wizard-sessions";

const STEPS = [
  {
    id: 1,
    label: "General Info",
    subSteps: ["Program Info", "Classes"],
  },
  {
    id: 2,
    label: "Permission Forms",
    subSteps: ["General Release", "Photo Release"],
    navGroups: [
      { label: "General Release", range: [0, 0] },
      { label: "Photo Release", range: [1, 1] },
    ],
  },
  {
    id: 3,
    label: "Student Info",
    subSteps: ["General Info", "Baptism Info", "Medical & Emergency", "More Info", "Custom Fields"],
    lastMainSubStep: 3,
    navGroups: [
      { label: "General Info", range: [0, 0] },
      { label: "Baptism Info", range: [1, 1] },
      { label: "Medical & Emergency", range: [2, 2] },
      { label: "More Info", range: [3, 4] },
    ],
  },
  {
    id: 4,
    label: "Pricing",
    subSteps: ["General Pricing", "Additional Fees", "Discounts & More", "Early & Late Pricing", "Member & Catechist Pricing"],
    lastMainSubStep: 2,
    navGroups: [
      { label: "General Pricing", range: [0, 0] },
      { label: "Additional Fees", range: [1, 1] },
      { label: "Discounts & More", range: [2, 4] },
    ],
  },
  {
    id: 5,
    label: "Confirmation",
    subSteps: ["Confirmation", "Loading", "Complete"],
    lastMainSubStep: 0,
  },
];

const defaultData = {
  generalInfo: {
    programName: `St. Mary's Religious Education ${new Date().getFullYear()}`,
    registrationDeadline: (() => {
      const now = new Date();
      const y = now.getFullYear();
      const m = now.getMonth(); // 0-indexed
      if (m < 7) return `${y}-08-01`;
      const lastDay = new Date(y, m + 1, 0).getDate();
      return `${y}-${String(m + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    })(),
    classes: ["", "", ""],
    showCapacity: false,
    capacities: ["20", "20", "20"],
  },
  permissionRelease: {
    generalReleaseCustom: false,
    generalReleaseDeclined: false,
    generalReleaseText: "",
    photoReleaseCustom: false,
    photoReleaseDeclined: false,
    photoReleaseText: "",
  },
  studentInfo: {
    address: true,
    birthday: true,
    gender: true,
    grade: true,
    school: true,
    shirtSize: false,
    baptism: null,
    firstCommunion: true,
    confirmation: false,
    reconciliation: false,
    allergiesMedical: true,
    emergencyContacts: true,
    previousParish: false,
    preferredLanguage: false,
    godparents: false,
    siblings: false,
    specialNeeds: false,
    parentVolunteer: false,
    massAttendance: false,
    envelopeNumber: false,
    transportationNeeds: false,
    tshirtSize: false,
    customFields: [],
    customFieldModes: {},
  },
  pricing: {
    baseFee: "",
    familyCap: false,
    familyCapValue: "",
    requirePayment: false,
    additionalFeesNeeded: false,
    additionalFeesDeclined: false,
    additionalFees: {},
    earlyBirdPricing: false,
    earlyBirdFee: "",
    earlyBirdFamilyCap: false,
    earlyBirdFamilyCapValue: "",
    earlyBirdDate: `${new Date().getFullYear()}-06-01`,
    latePricing: false,
    lateFee: "",
    lateFamilyCap: false,
    lateFamilyCapValue: "",
    lateDate: `${new Date().getFullYear()}-08-15`,
    memberPricing: false,
    memberFee: "",
    memberFamilyCap: false,
    memberFamilyCapValue: "",
    catechistPricing: false,
    catechistFee: "",
    catechistFamilyCap: false,
    catechistFamilyCapValue: "",
    advancedPricingRequested: false,
  },
};

function hasExistingSessions() {
  try {
    const stored = localStorage.getItem(SESSIONS_KEY);
    if (stored) {
      const arr = JSON.parse(stored);
      return Array.isArray(arr) && arr.length > 0;
    }
  } catch {}
  return false;
}

const defaultState = {
  isOpen: false,
  currentStep: 0,
  currentSubStep: 0,
  completedSteps: [],
  visitedSubSteps: {},
  highestStepReached: 0,
  committedProgramName: "",
  data: defaultData,
  activeSessionId: null,
  showSessionPicker: false,
  showOverview: false,
};

function loadSessions() {
  try {
    const stored = localStorage.getItem(SESSIONS_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return [];
}

function saveSessions(sessions) {
  try {
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
  } catch {}
}

function cleanEmptyClasses(s) {
  if (s.currentStep !== 1 || s.currentSubStep !== 1) return s;
  const classes = s.data.generalInfo.classes || [];
  const capacities = s.data.generalInfo.capacities || [];
  const kept = classes.reduce((acc, name, i) => {
    if (name.trim()) {
      acc.classes.push(name);
      acc.capacities.push(capacities[i] ?? "20");
    }
    return acc;
  }, { classes: [], capacities: [] });
  if (kept.classes.length === 0) return s;
  if (kept.classes.length === classes.length) return s;
  return {
    ...s,
    data: {
      ...s.data,
      generalInfo: { ...s.data.generalInfo, ...kept },
    },
  };
}

function ensureMinClasses(s) {
  if (s.currentStep !== 1 || s.currentSubStep !== 1) return s;
  const classes = s.data.generalInfo.classes || [];
  if (classes.length >= 1) return s;
  return {
    ...s,
    data: {
      ...s.data,
      generalInfo: {
        ...s.data.generalInfo,
        classes: [""],
        capacities: ["20"],
      },
    },
  };
}

export const WizardContext = createContext(null);

export default function WizardProvider({ children }) {
  // Always start fresh on page load (intro screen)
  const [state, setState] = useState(defaultState);
  const [modalOpen, setModalOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const backOverrideRef = useRef(null);
  const nextOverrideRef = useRef(null);
  const directionOverrideRef = useRef(null);
  const hasLeftIntroRef = useRef(false);

  // Reset collapsed when navigating away from the Complete view
  useEffect(() => {
    if (state.currentStep !== 5 || state.currentSubStep !== 2) {
      setCollapsed(false);
    }
  }, [state.currentStep, state.currentSubStep]);

  // Auto-save to active session — only once General Info is completed
  useEffect(() => {
    if (!state.activeSessionId || !state.completedSteps.includes(1)) return;
    const sessions = loadSessions();
    const sessionState = {
      currentStep: state.currentStep,
      currentSubStep: state.currentSubStep,
      completedSteps: state.completedSteps,
      visitedSubSteps: state.visitedSubSteps,
      highestStepReached: state.highestStepReached,
      committedProgramName: state.committedProgramName,
      data: state.data,
    };
    const name =
      state.committedProgramName || state.data.generalInfo.programName.trim() || "Untitled";
    const idx = sessions.findIndex((s) => s.id === state.activeSessionId);
    if (idx === -1) {
      // First save — create the entry
      sessions.push({
        id: state.activeSessionId,
        name,
        updatedAt: Date.now(),
        state: sessionState,
      });
    } else {
      sessions[idx].updatedAt = Date.now();
      sessions[idx].name = name;
      sessions[idx].state = sessionState;
    }
    saveSessions(sessions);
  }, [state]);

  const getSessions = useCallback(() => {
    return loadSessions().sort((a, b) => b.updatedAt - a.updatedAt);
  }, []);

  const startNewSession = useCallback(() => {
    hasLeftIntroRef.current = true;
    setState((s) => ({
      ...s,
      isOpen: true,
      currentStep: 0,
      currentSubStep: 0,
      showOverview: false,
      data: defaultData,
      completedSteps: [],
      committedProgramName: "",
      activeSessionId: null,
    }));
  }, []);

  const beginSession = useCallback(() => {
    // Just set up local state — don't persist until General Info is completed
    const id = String(Date.now());
    setState((s) => ({
      ...s,
      isOpen: true,
      currentStep: 1,
      currentSubStep: 0,
      completedSteps: [],
      committedProgramName: "",
      data: defaultData,
      activeSessionId: id,
      showSessionPicker: false,
      showOverview: false,
    }));
  }, []);

  const resumeSession = useCallback((id) => {
    const sessions = loadSessions();
    const session = sessions.find((s) => s.id === id);
    if (!session) return;
    setState((s) => ({
      ...s,
      ...session.state,
      isOpen: true,
      activeSessionId: id,
      showSessionPicker: false,
    }));
  }, []);

  const deleteSession = useCallback((id) => {
    const sessions = loadSessions().filter((s) => s.id !== id);
    saveSessions(sessions);
    // If we deleted the last session, go to intro
    if (sessions.length === 0) {
      setState((s) => ({
        ...s,
        currentStep: 0,
        currentSubStep: 0,
        completedSteps: [],
        committedProgramName: "",
        data: defaultData,
        activeSessionId: null,
        showSessionPicker: false,
      }));
    }
  }, []);

  const archiveSession = useCallback((id) => {
    const sessions = loadSessions();
    const idx = sessions.findIndex((s) => s.id === id);
    if (idx !== -1) {
      sessions[idx].archived = true;
      saveSessions(sessions);
    }
  }, []);

  const unarchiveSession = useCallback((id) => {
    const sessions = loadSessions();
    const idx = sessions.findIndex((s) => s.id === id);
    if (idx !== -1) {
      sessions[idx].archived = false;
      saveSessions(sessions);
    }
  }, []);

  const setShowSessionPicker = useCallback((show) => {
    hasLeftIntroRef.current = true;
    setState((s) => ({
      ...s,
      showSessionPicker: show,
      ...(show ? { currentStep: 0, currentSubStep: 0 } : {}),
    }));
  }, []);

  const setShowOverview = useCallback((show) => {
    setState((s) => ({
      ...s,
      showOverview: show,
    }));
  }, []);

  const openWizard = useCallback(() => {
    setState((s) => ({ ...s, isOpen: true }));
  }, []);

  const closeWizard = useCallback(() => {
    setCollapsed(false);
    setState((s) => ({ ...s, isOpen: false }));
  }, []);

  const collapseOverlay = useCallback(() => {
    setCollapsed(true);
  }, []);

  const expandOverlay = useCallback(() => {
    setCollapsed(false);
  }, []);

  const finishSession = useCallback(() => {
    setState((s) => {
      // Mark session as finished in localStorage
      if (s.activeSessionId) {
        const sessions = loadSessions();
        const idx = sessions.findIndex((ses) => ses.id === s.activeSessionId);
        if (idx !== -1) {
          sessions[idx].finished = true;
          saveSessions(sessions);
        }
      }
      return { ...s, isOpen: false };
    });
  }, []);

  const updateField = useCallback((section, field, value) => {
    setState((s) => ({
      ...s,
      data: {
        ...s.data,
        [section]: {
          ...s.data[section],
          [field]: value,
        },
      },
    }));
  }, []);

  const goToStep = useCallback((step, subStep = 0, markCurrentComplete = false) => {
    setState((s) => {
      const cleaned = cleanEmptyClasses(s);
      const commitName =
        cleaned.currentStep === 1
          ? cleaned.data.generalInfo.programName.trim()
          : cleaned.committedProgramName;
      const completed =
        markCurrentComplete && !cleaned.completedSteps.includes(cleaned.currentStep)
          ? [...cleaned.completedSteps, cleaned.currentStep]
          : cleaned.completedSteps;
      return ensureMinClasses({
        ...cleaned,
        currentStep: step,
        currentSubStep: subStep,
        committedProgramName: commitName,
        completedSteps: completed,
        highestStepReached: Math.max(cleaned.highestStepReached, step),
      });
    });
  }, []);

  const nextSubStep = useCallback(() => {
    if (nextOverrideRef.current) {
      const handled = nextOverrideRef.current();
      if (handled) return;
    }
    setState((s) => {
      const cleaned = cleanEmptyClasses(s);
      const stepDef = STEPS.find((st) => st.id === cleaned.currentStep);
      if (!stepDef) return cleaned;

      const maxSub = stepDef.lastMainSubStep ?? Math.max(0, stepDef.subSteps.length - 1);
      const commitName =
        cleaned.currentStep === 1
          ? cleaned.data.generalInfo.programName.trim()
          : cleaned.committedProgramName;

      const visitedKey = `${cleaned.currentStep}-${cleaned.currentSubStep}`;
      const visited = { ...cleaned.visitedSubSteps, [visitedKey]: true };

      if (cleaned.currentSubStep < maxSub) {
        return ensureMinClasses({ ...cleaned, currentSubStep: cleaned.currentSubStep + 1, committedProgramName: commitName, visitedSubSteps: visited });
      }

      // Move to next step
      const nextStep = cleaned.currentStep + 1;
      if (nextStep > STEPS.length) return cleaned;

      const completed = cleaned.completedSteps.includes(cleaned.currentStep)
        ? cleaned.completedSteps
        : [...cleaned.completedSteps, cleaned.currentStep];

      return {
        ...cleaned,
        currentStep: nextStep,
        currentSubStep: 0,
        completedSteps: completed,
        committedProgramName: commitName,
        highestStepReached: Math.max(cleaned.highestStepReached, nextStep),
        visitedSubSteps: visited,
      };
    });
  }, []);

  const setBackOverride = useCallback((fn) => {
    backOverrideRef.current = fn;
  }, []);

  const setNextOverride = useCallback((fn) => {
    nextOverrideRef.current = fn;
  }, []);

  const prevSubStep = useCallback(() => {
    if (backOverrideRef.current) {
      const handled = backOverrideRef.current();
      if (handled) return;
    }
    setState((s) => {
      if (s.currentSubStep > 0) {
        let targetSub = s.currentSubStep - 1;
        return ensureMinClasses({ ...s, currentSubStep: targetSub });
      }

      // Move to previous step's last sub-step
      const prevStep = s.currentStep - 1;
      if (prevStep < 0) return s;

      // Going back to intro
      if (prevStep === 0) {
        return { ...s, currentStep: 0, currentSubStep: 0 };
      }

      const prevStepDef = STEPS.find((st) => st.id === prevStep);
      let lastSub = prevStepDef
        ? prevStepDef.lastMainSubStep ?? Math.max(0, prevStepDef.subSteps.length - 1)
        : 0;

      return ensureMinClasses({
        ...s,
        currentStep: prevStep,
        currentSubStep: lastSub,
      });
    });
  }, []);

  const resetWizard = useCallback(() => {
    setState({ ...defaultState });
    localStorage.removeItem(SESSIONS_KEY);
  }, []);

  const isStepValid = useCallback(
    (stepId, data) => {
      if (stepId === 1) {
        const hasName = (data.generalInfo.programName || "").trim().length >= 3;
        const hasClass = (data.generalInfo.classes || []).some(c => c.trim().length > 0);
        return hasName && hasClass;
      }
      return true;
    },
    []
  );

  const isSubStepValid = useCallback(
    (stepId, subStep, data) => {
      if (stepId === 1) {
        if (subStep === 0) return (data.generalInfo.programName || "").trim().length >= 3;
        if (subStep === 1) {
          const hasClass = (data.generalInfo.classes || []).some(c => c.trim().length > 0);
          if (!hasClass) return false;
          if (data.generalInfo.showCapacity) {
            const classes = data.generalInfo.classes || [];
            const caps = data.generalInfo.capacities || [];
            const hasMissingCap = classes.some((c, i) => c.trim() && !(caps[i] || "").trim());
            if (hasMissingCap) return false;
          }
          return true;
        }
      }
      if (stepId === 2) {
        if (subStep === 0) {
          if (data.permissionRelease.generalReleaseDeclined) return true;
          if (!data.permissionRelease.generalReleaseCustom) return false;
          const text = (data.permissionRelease.generalReleaseText || "").replace(/<[^>]*>/g, "").trim();
          return text.length > 0;
        }
        if (subStep === 1) {
          if (data.permissionRelease.photoReleaseDeclined) return true;
          if (!data.permissionRelease.photoReleaseCustom) return false;
          const text = (data.permissionRelease.photoReleaseText || "").replace(/<[^>]*>/g, "").trim();
          return text.length > 0;
        }
        return true;
      }
      if (stepId === 3) {
        if (subStep === 1) return data.studentInfo.baptism !== null;
        return !!state.visitedSubSteps[`${stepId}-${subStep}`];
      }
      if (stepId === 4) {
        if (subStep === 0) {
          const base = parseFloat(data.pricing.baseFee);
          if (!data.pricing.baseFee || isNaN(base) || base < 2) return false;
          if (data.pricing.familyCap) {
            const cap = parseFloat(data.pricing.familyCapValue);
            if (!data.pricing.familyCapValue || isNaN(cap) || cap < 2 || cap < base) return false;
          }
          return true;
        }
        if (subStep === 1) {
          if (data.pricing.additionalFeesDeclined) return true;
          if (!data.pricing.additionalFeesNeeded) return false;
          const fees = data.pricing.additionalFees || {};
          for (const key of Object.keys(fees)) {
            const val = parseFloat(fees[key]);
            if (!fees[key] || isNaN(val) || val < 2) return false;
          }
          return true;
        }
        if (subStep === 3) {
          const p = data.pricing;
          const validateSection = (enabled, fee, cap, capValue) => {
            if (!enabled) return true;
            const f = parseFloat(fee);
            if (!fee || isNaN(f) || f < 2) return false;
            if (cap) {
              const c = parseFloat(capValue);
              if (!capValue || isNaN(c) || c < 2 || c < f) return false;
            }
            return true;
          };
          if (p.earlyBirdPricing && !p.earlyBirdDate) return false;
          if (p.latePricing && !p.lateDate) return false;
          if (!validateSection(p.earlyBirdPricing, p.earlyBirdFee, p.earlyBirdFamilyCap, p.earlyBirdFamilyCapValue)) return false;
          if (!validateSection(p.latePricing, p.lateFee, p.lateFamilyCap, p.lateFamilyCapValue)) return false;
          return true;
        }
        if (subStep === 4) {
          const p = data.pricing;
          const validateSection = (enabled, fee, cap, capValue) => {
            if (!enabled) return true;
            const f = parseFloat(fee);
            if (!fee || isNaN(f) || f < 2) return false;
            if (cap) {
              const c = parseFloat(capValue);
              if (!capValue || isNaN(c) || c < 2 || c < f) return false;
            }
            return true;
          };
          if (!validateSection(p.memberPricing, p.memberFee, p.memberFamilyCap, p.memberFamilyCapValue)) return false;
          if (!validateSection(p.catechistPricing, p.catechistFee, p.catechistFamilyCap, p.catechistFamilyCapValue)) return false;
          return true;
        }
      }
      return true;
    },
    [state.visitedSubSteps]
  );

  const isCurrentStepValid = useMemo(
    () => {
      if (state.currentStep === 3 && state.currentSubStep !== 1) return true;
      return isSubStepValid(state.currentStep, state.currentSubStep, state.data);
    },
    [isSubStepValid, state.currentStep, state.currentSubStep, state.data]
  );

  const value = {
    ...state,
    collapsed,
    steps: STEPS,
    openWizard,
    closeWizard,
    collapseOverlay,
    expandOverlay,
    finishSession,
    updateField,
    goToStep,
    nextSubStep,
    prevSubStep,
    setBackOverride,
    setNextOverride,
    directionOverrideRef,
    resetWizard,
    isStepValid,
    isSubStepValid,
    isCurrentStepValid,
    getSessions,
    startNewSession,
    beginSession,
    resumeSession,
    deleteSession,
    archiveSession,
    unarchiveSession,
    setShowSessionPicker,
    setShowOverview,
    hasLeftIntroRef,
    modalOpen,
    setModalOpen,
  };

  return (
    <WizardContext.Provider value={value}>{children}</WizardContext.Provider>
  );
}
